<template>
  <div class="ns-stripe-donations-container">
    <div
      class="g-recaptcha"
      :data-siteKey="captchaKey"
      data-size="invisible"
    ></div>
    <form class="stripe-form" novalidate>
      <div class="form-section">
        <h4 class="section-title">{{ personalInfoTitle }}</h4>
        <label for="first-name" class="label">{{ firstNameLabel }}</label>
        <input
          id="first-name"
          v-model="firstName"
          class="input"
          type="text"
          required
        />
        <label for="last-name" class="label">{{ lastNameLabel }}</label>
        <input
          id="last-name"
          v-model="lastName"
          class="input"
          type="text"
          required
        />
        <label
          for="email"
          class="label"
          :class="{ 'error-label': showEmailError }"
          >{{ emailLabel }}</label
        >
        <input
          id="email"
          ref="emailRef"
          v-model="email"
          class="input"
          :class="{ 'show-errors': showErrors }"
          type="email"
          required
        />
        <div v-if="showEmailError" class="error-helper">
          {{ emailErrorMessage }}
        </div>
        <label for="affiliate-id" class="label"
          >{{ nuskinIdLabel }}
          <span class="optional">({{ optionalLabel }})</span></label
        >
        <input
          id="affiliate-id"
          v-model="affiliateId"
          class="input"
          type="text"
        />
      </div>
      <div class="separator"></div>
      <div class="form-section">
        <h4 class="section-title">{{ amountTitle }}</h4>
        <div class="donation-amounts" :style="donationBtnsStyle">
          <button
            v-for="amt in buttonAmounts"
            :key="amt.value"
            type="button"
            :class="[
              amt.value === buttonAmount ? 'hit-solid' : 'primary-outline'
            ]"
            class="btn-primary donation-amount"
            @click="updateAmount(amt.value)"
          >
            {{ amt.displayText }}
          </button>
        </div>
        <label
          for="custom-amount"
          class="label"
          :class="{ 'error-label': showCustomAmountError }"
          >{{ amountLabel }}</label
        >
        <div
          class="input-container"
          :class="{ 'error-input': showCustomAmountError }"
        >
          <span class="currency">$</span>
          <Money
            id="custom-amount"
            v-model="customAmount"
            class="reset-input"
            type="text"
            v-bind="money"
          />
        </div>
        <div v-if="showCustomAmountError" class="error-helper">
          {{ minDonationAmountLabel }}
        </div>
        <div class="section-spacer" />
        <h4 class="section-title">{{ donateTitle }}</h4>
        <!-- Alipay not supported yet. Uncomment to add multiple payment methods back in. -->
        <div v-if="paymentMethod === 'card' && stripeElementsReady">
          <label
            for="card-number"
            class="label"
            :class="{ 'error-label': numberInfo.error }"
            >{{ cardNumberLabel }}</label
          >
          <CardNumber
            id="card-number"
            ref="cardNumber"
            class="stripe-element"
            :class="{
              focused: stripeFocusId === 'card-number',
              'error-input': numberInfo.error
            }"
            :stripe="stripeKey"
            :options="stripeOptions"
            @change="numberInfo = $event"
            @focus="stripeFocusId = 'card-number'"
            @blur="stripeFocusId = ''"
          />
          <div v-if="numberInfo.error" class="error-helper">
            {{ numberInfo.error.message }}
          </div>
          <div class="expiry-cvc-row">
            <div class="row-item">
              <label
                for="card-expiry"
                class="label"
                :class="{ 'error-label': expiryInfo.error }"
                >{{ expiryLabel }}</label
              >
              <CardExpiry
                id="card-expiry"
                ref="cardExpiry"
                class="stripe-element"
                :class="{
                  focused: stripeFocusId === 'card-expiry',
                  'error-input': expiryInfo.error
                }"
                :stripe="stripeKey"
                :options="stripeOptions"
                @change="expiryInfo = $event"
                @focus="stripeFocusId = 'card-expiry'"
                @blur="stripeFocusId = ''"
              />
              <div v-if="expiryInfo.error" class="error-helper">
                {{ expiryInfo.error.message }}
              </div>
            </div>
            <div class="row-item">
              <label
                for="card-cvc"
                class="label"
                :class="{ 'error-label': cvcInfo.error }"
                >{{ cvcLabel }}</label
              >
              <CardCvc
                id="card-cvc"
                ref="cardCvc"
                class="stripe-element"
                :class="{
                  focused: stripeFocusId === 'card-cvc',
                  'error-input': cvcInfo.error
                }"
                :stripe="stripeKey"
                :options="stripeOptions"
                @change="cvcInfo = $event"
                @focus="stripeFocusId = 'card-cvc'"
                @blur="stripeFocusId = ''"
              />
              <div v-if="cvcInfo.error" class="error-helper">
                {{ cvcInfo.error.message }}
              </div>
            </div>
          </div>
        </div>
        <!-- Uncomment when alipay is supported -->
        <button
          :disabled="!complete"
          type="button"
          class="btn-primary hit-solid fluid"
          :class="{ 'button-processing': loading }"
          @click="pay"
        >
          <span v-if="!loading && paymentMethod === 'card'"
            >{{ submitLabel
            }}{{ amount ? ` $${formatMoney(amount, 2)}` : "" }}</span
          >
          <!-- Uncomment when alipay is supported -->
          <span v-if="loading" class="loading"
            >{{ processingText }}<span>.</span><span>.</span
            ><span>.</span></span
          >
        </button>
      </div>
    </form>
    <NsToast
      ref="toast"
      :success="!!processingSuccess"
      :error="!!processingError"
      :message="processingSuccess || processingError"
      :show-trigger="toastOpen"
      persistent
    />
    <div class="loader">
      <ns-global-loader-vue :open="loading">
        <span v-if="loadingMessage">{{ loadingMessage }}</span>
        <span v-if="!loadingMessage" class="loading"
          >{{ processingText }}<span>.</span><span>.</span><span>.</span></span
        >
      </ns-global-loader-vue>
    </div>
  </div>
</template>

<script>
import {
  CardNumber,
  CardExpiry,
  CardCvc,
  createToken
} from "vue-stripe-elements-plus";
import { StripePaymentService } from "@nuskin/ns-shop";
import { NsToast } from "@nuskin/design-components";
import { Money } from "v-money";

export default {
  name: "NsStripeDonationForm",
  components: {
    CardNumber,
    CardExpiry,
    CardCvc,
    Money,
    NsToast
  },
  props: {
    /**
     * The site key for reCaptcha
     */
    captchaKey: {
      type: String,
      default: ""
    },
    /**
     * The account key for stripe
     */
    stripeKey: {
      type: String,
      default: ""
    },
    /**
     * Translated label
     */
    submitLabel: {
      type: String,
      default: "Donate"
    },
    /**
     * Translated success message
     */
    continueAlipayLabel: {
      type: String,
      default: "Continue with Alipay"
    },
    /**
     * Translated label
     */
    personalInfoTitle: {
      type: String,
      default: "Personal Information"
    },
    /**
     * Translated label
     */
    nuskinIdLabel: {
      type: String,
      default: "Nu Skin ID"
    },
    /**
     * Translated label
     */
    optionalLabel: {
      type: String,
      default: "optional"
    },
    /**
     * Translated label
     */
    firstNameLabel: {
      type: String,
      default: "First Name"
    },
    /**
     * Translated label
     */
    lastNameLabel: {
      type: String,
      default: "Last Name"
    },
    /**
     * Translated label
     */
    amountTitle: {
      type: String,
      default: "How much would you like to donate?"
    },
    /**
     * Translated label
     */
    amountLabel: {
      type: String,
      default: "Custom Amount"
    },
    /**
     * Translated label
     */
    amounts: {
      type: String,
      default: JSON.stringify([
        {
          value: 25,
          displayText: "$25"
        },
        {
          value: 50,
          displayText: "$50"
        }
      ])
    },
    /**
     * Translated label
     */
    donateTitle: {
      type: String,
      default: "Donate with Card"
    },
    /**
     * Translated label
     */
    paymentMethodTitle: {
      type: String,
      default: "Select Payment Method"
    },
    /**
     * Translated label
     */
    cardLabel: {
      type: String,
      default: "Credit Card"
    },
    /**
     * Translated label
     */
    alipayLabel: {
      type: String,
      default: "Alipay"
    },
    /**
     * Translated label
     */
    alipayRedirectNotice: {
      type: String,
      default:
        "You will be redirected to the Alipay site to complete your payment."
    },

    /**
     * Translated label
     */
    emailLabel: {
      type: String,
      default: "Email Address"
    },
    /**
     * Translated label
     */
    emailErrorMessage: {
      type: String,
      default: "Please use a valid email"
    },
    /**
     * Translated label
     */
    cardNumberLabel: {
      type: String,
      default: "Card Number"
    },
    /**
     * Translated label
     */
    expiryLabel: {
      type: String,
      default: "Expiration Date"
    },
    /**
     * Translated label
     */
    cvcLabel: {
      type: String,
      default: "Security Code"
    },
    /**
     * Translated error message for a minimum amount
     */
    minDonationAmountLabel: {
      type: String,
      default: "Donation amount must be at least $1.00"
    },
    /**
     * Url that will redirect upon a successful donation reponse
     */
    thankYouUrl: {
      type: String,
      default: ""
    },
    /**
     * Translated success message
     */
    processingText: {
      type: String,
      default: "Processing"
    },
    /**
     * Translated success message
     */
    checkingAlipayText: {
      type: String,
      default: "Checking Alipay for payment confirmation."
    },
    /**
     * Translated success message
     */
    successMessage: {
      type: String,
      default: "Your payment was successful!"
    },
    /**
     * Translated failed message
     */
    errorMessage: {
      type: String,
      default: "Something went wrong with your payment."
    }
  },
  data() {
    return {
      /**
       * Money options
       */
      money: {
        decimal: ".",
        thousands: ",",
        precision: 2,
        masked: false
      },
      /**
       * Amount of the donation
       */
      amount: 0,
      /**
       * Button amount of the donation
       */
      buttonAmount: null,
      /**
       * Custom amount of the donation
       */
      customAmount: 0,
      /**
       * Email address from the donar
       */
      email: "",
      /**
       * Donars first name
       */
      firstName: "",
      /**
       * Donars last name
       */
      lastName: "",
      /**
       * Donars distriburtor ID
       */
      affiliateId: "",
      /**
       * Payment method
       */
      paymentMethod: "card",
      /**
       * Donars credit card number info {complete, error, etc}
       */
      numberInfo: {},
      /**
       * Donars credit card experation date info {complete, error, etc}
       */
      expiryInfo: {},
      /**
       * Donars CVC from credit card info {complete, error, etc}
       */
      cvcInfo: {},
      /**
       * Variable to set state if all required fields are filled out
       */
      complete: false,
      /**
       * Loading state when processing payment
       */
      loading: false,
      /**
       * Stripe element id if focused
       */
      stripeFocusId: "",
      /**
       * After form submit show errors if any
       */
      showErrors: "",
      /**
       * Email invalid
       */
      emailError: false,
      /**
       * Stripe processing success message
       */
      processingSuccess: "",
      /**
       * Stripe processing error message
       */
      processingError: "",
      /**
       * Styling options for the stripe elements such as typography and color
       */
      stripeOptions: {
        elements: {
          fonts: [
            {
              cssSrc: "https://use.typekit.net/drl7neu.css"
            }
          ]
        },
        style: {
          base: {
            fontSize: "16px",
            fontWeight: 300,
            fontFamily:
              '"proxima-nova","OpenSans","Pridi","NotoSans","NotoSansSC","NotoSansHebrew","NotoSansJapanese","Hiragino Kaku Gothic Pro","Yu Gothic Medium","Yu Gothic",Meiryo,"source-han-sans-japanese","source-han-sans-korean","source-han-sans-simplified-c",Helvetica,Arial,sans-serif'
          },
          invalid: {
            color: "#b2140a"
          }
        }
      },
      /**
       * Used for actual array value of amounts, gets parsed in mounted
       * */
      buttonAmounts: [],
      /**
       * loading message
       */
      loadingMessage: "",
      /**
       * stripe elements ready to load
       */
      stripeElementsReady: false,
      toastOpen: false
    };
  },
  computed: {
    /**
     * Causes donation buttons to break into separate lines at certain lengths
     * */
    donationBtnsStyle: function() {
      let numOfCol = this.buttonAmounts.length;
      if (this.buttonAmounts.length > 8) {
        numOfCol = 5;
      } else if (this.buttonAmounts.length > 5) {
        numOfCol = 4;
      }
      return { gridTemplateColumns: `repeat(${numOfCol}, 1fr)` };
    },
    showCustomAmountError: function() {
      return this.showErrors && this.customAmount != 0 && this.customAmount < 1;
    },
    showEmailError: function() {
      return this.showErrors && this.emailError;
    }
  },
  watch: {
    numberInfo() {
      this.validateFormCompletion();
    },
    expiryInfo() {
      this.validateFormCompletion();
    },
    cvcInfo() {
      this.validateFormCompletion();
    },
    firstName() {
      this.validateFormCompletion();
    },
    lastName() {
      this.validateFormCompletion();
    },
    paymentMethod(value) {
      // reset card info
      if (value !== "card") {
        this.numberInfo = {};
        this.expiryInfo = {};
        this.cvcInfo = {};
      }
      this.validateFormCompletion();
    },
    email() {
      if (this.$refs.emailRef.validity.typeMismatch) {
        this.emailError = true;
      } else {
        this.emailError = false;
      }
      this.validateFormCompletion();
    },
    amount() {
      this.validateFormCompletion();
    },
    customAmount(amt) {
      this.amount = amt || this.buttonAmount;
      if (amt) {
        this.buttonAmount = null;
      }
    },
    processingSuccess(msg) {
      if (msg) {
        this.toastOpen = true;
      } else {
        this.toastOpen = false;
      }
    },
    processingError(msg) {
      if (msg) {
        this.toastOpen = true;
      } else {
        this.toastOpen = false;
      }
    },
    loading(loading) {
      if (!loading) {
        this.loadingMessage = "";
      }
    }
  },
  mounted() {
    // to address iOS bug: stripe elements need to load after mount. See...
    // https://github.com/fromAtoB/vue-stripe-elements/issues/68
    this.stripeElementsReady = true;

    // set inital button amounts
    try {
      this.buttonAmounts = JSON.parse(this.amounts);
    } catch (e) {
      console.warn("Could not parse button amounts from amounts property", e);
    }

    // if there is a search param of 'source' then this page was reached by
    // a redirect from the alipay payment page and we start polling stripe
    // to see if the charge was successful.
    const url = new URL(window.location.href);

    // set inital input values to show what was previously entered
    this.firstName = url.searchParams.get("firstName") || "";
    this.lastName = url.searchParams.get("lastName") || "";
    this.email = url.searchParams.get("email") || "";
    this.affiliateId = url.searchParams.get("affiliateId") || "";
    if (url.searchParams.get("paymentMethod")) {
      this.paymentMethod = url.searchParams.get("paymentMethod");
    }

    const pollIfRedirected = async () => {
      if (
        url.searchParams.get("source") &&
        url.searchParams.get("client_secret")
      ) {
        // Update the interface to display the processing screen.
        this.loading = true;
        this.loadingMessage = this.checkingAlipayText;

        try {
          const { source } = await window
            .Stripe(this.stripeKey)
            .retrieveSource({
              id: url.searchParams.get("source"),
              client_secret: url.searchParams.get("client_secret")
            });

          // Poll the PaymentIntent status.
          this.pollPaymentIntentStatus(source.metadata.paymentIntent);
        } catch (error) {
          this.loading = false;
          console.error("Error with retrieving source from Stripe");
          this.processingError = this.errorMessage;
        }
      }
    };
    pollIfRedirected();
  },
  methods: {
    /**
     * Gets called when a required field is updated
     */
    validateFormCompletion() {
      switch (this.paymentMethod) {
        case "card":
          this.complete =
            this.numberInfo.complete &&
            this.expiryInfo.complete &&
            this.cvcInfo.complete &&
            this.firstName &&
            this.lastName &&
            this.email &&
            this.amount;
          break;
        case "alipay":
          this.complete =
            this.firstName && this.lastName && this.email && this.amount;
          break;
        default:
          this.complete = false;
      }
    },
    /**
     * Gets called when an amount is updated
     */
    updateAmount(amt) {
      this.buttonAmount = amt;
      this.customAmount = 0;
      this.amount = +amt;
    },
    // formats to currency for the Donate button
    formatMoney(amount, decimalCount = 2, decimal = ".", thousands = ",") {
      try {
        decimalCount = Math.abs(decimalCount);
        decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

        const negativeSign = amount < 0 ? "-" : "";
        amount = Math.abs(Number(amount) || 0).toFixed(decimalCount);
        let i = parseInt(amount).toString();
        let j = i.length > 3 ? i.length % 3 : 0;

        return (
          negativeSign +
          (j ? i.substr(0, j) + thousands : "") +
          i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
          (decimalCount
            ? decimal +
              Math.abs(amount - i)
                .toFixed(decimalCount)
                .slice(2)
            : "")
        );
      } catch (e) {
        console.log(e);
      }
    },
    /**
     * Method that is called once we get the payment token from stripe
     */
    pay() {
      this.processingError = "";
      this.processingSuccess = "";
      if (
        this.emailError ||
        (this.customAmount != 0 && this.customAmount < 1)
      ) {
        this.showErrors = true;
      } else if (this.complete) {
        if (this.amount >= 1) {
          this.loading = true;
          if (this.paymentMethod === "card") {
            this.payWithCard();
          } else if (this.paymentMethod === "alipay") {
            this.payWithAlipay();
          }
        } else {
          // show error if after double checking, amount is still less than $1
          this.processingError = this.minDonationAmountLabel;
        }
      }
    },
    // paying with a card
    async payWithCard() {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(this.captchaKey, {
            action: "submit"
          })
          .then(captcha => {
            createToken()
              .then(
                async response =>
                  await this.createStripeSource(response, captcha)
              )
              .catch(error => {
                this.loading = false;
                console.error(
                  "Error with payment method - createToken: " + error.toString()
                );
                this.processingError = this.errorMessage;
              });
          })
          .catch(err => {
            console.error(err);
          });
      });
    },
    async createStripeSource(response, captcha) {
      if (response.error) {
        this.loading = false;
        console.error("Error with payment method - createToken");
        this.processingError = response.error.message || this.errorMessage;
        return null;
      }

      // turns the token into a source in order to add email to owner
      try {
        const { source } = await window.Stripe(this.stripeKey).createSource({
          type: "card",
          currency: "usd",
          owner: {
            email: this.email
          },
          token: response.token.id
        });
        if (!source) {
          this.loading = false;
          console.error("Error with converting to source - createSource");
          this.processingError = this.errorMessage;
          return null;
        }

        source.captcha = captcha;

        // charge the source
        const { data } = await StripePaymentService.postPaymentTokenToStripe(
          this.amount,
          source.id,
          this.email,
          this.firstName,
          this.lastName,
          this.affiliateId,
          source.captcha
        );
        // show successful page/message
        if (data && data.captured) {
          this.showChargeSuccess();
        } else {
          // charge not captured (not sure if data.messages will ever exist)
          this.processingError = (data && data.messages) || this.errorMessage;
        }
      } catch (error) {
        // charge failed
        const message =
          ((error.response || {}).data || {}).message || error.message;
        this.processingError = message;
      } finally {
        this.loading = false;
      }
    },
    // paying with alipay
    async payWithAlipay() {
      // this will start the alipay process. To confirm success, check 'mounted' function
      // construct return url (using 'set' in case user makes 2nd donation)
      const url = new URL(window.location.href);
      url.searchParams.set("firstName", this.firstName);
      url.searchParams.set("lastName", this.lastName);
      url.searchParams.set("email", this.email);
      url.searchParams.set("affiliateId", this.affiliateId);
      url.searchParams.set("amount", this.amount);
      url.searchParams.set("paymentMethod", "alipay");
      url.searchParams.delete("client_secret");
      url.searchParams.delete("source");

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(this.captchaKey, {
            action: "submit"
          })
          .then(async captcha => {
            try {
              // create a payment intent and add it to source metadata
              const {
                data: paymentIntent
              } = await StripePaymentService.createPaymentIntent(
                this.amount,
                this.email,
                this.firstName,
                this.lastName,
                this.affiliateId,
                captcha
              );
              // create the source
              const response = await window
                .Stripe(this.stripeKey)
                .createSource({
                  type: "alipay",
                  amount: paymentIntent.amount,
                  currency: paymentIntent.currency,
                  redirect: {
                    return_url: url.href
                  },
                  owner: {
                    email: this.email
                  },
                  metadata: {
                    paymentIntent: paymentIntent.id
                  }
                });
              // navigate to alipay page
              if (response.source && response.source.redirect.url) {
                window.location.assign(response.source.redirect.url);
              } else if (response.error) {
                this.processingError = response.error.message;
              } else {
                this.processingError = this.errorMessage;
              }
              this.loading = false;
            } catch (error) {
              console.log({ error });
              const message =
                ((error.response || {}).data || {}).message || error.message;
              this.loading = false;
              this.processingError = message || this.errorMessage;
            }
          });
      });
    },
    // call after a payment is successful to show success indication
    showChargeSuccess() {
      if (this.thankYouUrl && this.thankYouUrl !== "#") {
        // go to thankyou page
        window.location = this.thankYouUrl;
      } else {
        // With no thankYouUrl -> clear amount, show success message
        this.buttonAmount = null;
        this.customAmount = 0;
        this.amount = 0;
        this.processingSuccess = this.successMessage;
      }
    },
    // for alipay, this will poll stripe to see if charge (paymentIntent.confirm()) was successful
    async pollPaymentIntentStatus(
      paymentIntentId,
      timeout = 15000,
      interval = 500,
      start = Date.now()
    ) {
      const endStates = ["succeeded", "processing", "canceled"];
      // Retrieve the PaymentIntent status from our server.
      const {
        data: paymentIntent
      } = await StripePaymentService.getPaymentIntent(paymentIntentId);
      if (
        !endStates.includes(paymentIntent.status) &&
        Date.now() < start + timeout
      ) {
        // Not done yet. Let's wait and check again.
        setTimeout(
          this.pollPaymentIntentStatus,
          interval,
          paymentIntentId,
          timeout,
          interval,
          start
        );
      } else {
        this.handlePaymentResult(paymentIntent);
        if (!endStates.includes(paymentIntent.status)) {
          // Status has not changed yet. Let's time out.
          console.warn(new Error("Polling timed out."));
          this.processingError =
            "There might be an error. Refresh page to keep checking.";
        }
      }
    },
    handlePaymentResult(paymentIntent) {
      this.loading = false;

      if (paymentIntent.status === "succeeded") {
        // Success! Payment is confirmed. Update the interface to display the confirmation screen.
        this.showChargeSuccess();
      } else if (paymentIntent.status === "processing") {
        this.processingError =
          "Still processing. You may refresh the page to recheck the status.";
      } else {
        // Payment has failed.
        this.processingError = this.errorMessage;
      }
    },
    async getCaptcha() {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(this.captchaKey, {
            action: "submit"
          })
          .then(token => {
            return token;
          })
          .catch(() => {
            return "";
          });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@nuskin/ns-core-styles/src/scss/global.mixins";
@import "~@nuskin/ns-core-styles/src/scss/globalVariables";

.ns-stripe-donations-container {
  margin: 0 auto;
  padding: 30px 0px 60px;
  box-sizing: border-box;
}
.stripe-form {
  display: flex;
  flex-direction: column;
}
.form-section {
  flex: 1 0 0;
  margin-bottom: 15px;
  padding: 0 20px;
}
.separator {
  display: none;
  margin: 39px 0 100px;
  border: 1px solid rgb(206, 208, 208);
  width: 2px;
  min-height: 331px;
  height: auto;
}
.section-spacer {
  height: 13px;
}
.label {
  display: block;
  font-size: 16px;
  line-height: 22px;
  margin-bottom: 4px;
  &.error-label {
    color: var(--color-error);
  }
}
.optional {
  color: rgb(99, 102, 106);
  font-size: 14px;
}
input[type="text"].input,
input[type="email"].input,
.input {
  padding: 11px 15px 10px;
  margin-bottom: 15px;
  border: 1px solid rgb(99, 102, 106);
  border-radius: 0px;
  width: 100%;
  color: #272525;
  font-size: 16px;
  font-weight: normal;
  line-height: 22px;
  transition: border 0.15s ease-in-out;
  &:focus {
    font-size: 16px;
    box-shadow: 0 0 5px 2px rgba(113, 35, 217, 0.2);
  }
  &.error-input,
  &.show-errors:invalid {
    color: var(--color-error);
    border: solid 1px var(--color-error);
    border-left: solid 6px var(--color-error);
  }
}
.input-container {
  @extend .input;
  display: flex;
  align-items: center;
  &:focus-within {
    border: 1px solid #008ab0;
    box-shadow: rgba(113, 35, 217, 0.2) 0px 0px 5px 2px;
  }
  &.error-input {
    border: solid 1px var(--color-error);
    border-left: solid 6px var(--color-error);
    input {
      color: var(--color-error);
    }
  }
}
input[type="text"].reset-input {
  @extend .input;
  margin: 0;
  border: none;
  padding: 0;
  padding-left: 5px;
  height: 16px;
  min-height: 16px;
  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
}
.stripe-element {
  padding: 12px 15px;
  margin-bottom: 15px;
  border: 1px solid rgb(99, 102, 106);
  border-radius: 0px;
  width: 100%;
  font-size: 16px;
  font-weight: normal;
  transition: border 0.15s ease-in-out;
  // :focus-within does not work with stripe elements
  &.focused {
    border: 1px solid #008ab0;
    box-shadow: rgba(113, 35, 217, 0.2) 0px 0px 5px 2px;
  }
  &.error-input {
    border: solid 1px var(--color-error);
    border-left: solid 6px var(--color-error);
  }
}
.error-helper {
  margin: -15px 0 10px;
  color: var(--color-error);
  font-size: 14px;
  line-height: 20px;
}
.section-title {
  margin-bottom: 15px;
  color: rgb(39, 37, 37);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}
.donation-amounts {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  margin: 0 -6px;
}
button.donation-amount {
  margin: 0 6px 15px;
  padding: 13px 4px;
  &:active {
    padding: 12px 3px;
    border-width: 2px;
  }
}
.radio-div {
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  width: 100%;
}
.radio-label {
  display: block;
  position: relative;
  padding-left: 35px;
  margin-left: 25px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  line-height: 25px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  &:first-child {
    margin-left: 0;
  }
  /* On mouse-over, add a grey background color to dot */
  &:hover input:not(:checked) ~ .radio-mark:after {
    display: block;
    background-color: #ccc;
  }
  /* When the radio button is checked, Show the indicator (dot/circle) */
  & input:checked ~ .radio-mark:after {
    display: block;
  }
}
.radio-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}
.radio-mark {
  position: absolute;
  top: 0;
  left: 0;
  border: 1px solid rgb(39, 37, 37);
  border-radius: 50%;
  height: 24px;
  width: 24px;
  background-color: #fff;
  /* Create the indicator (the dot/circle - hidden when not checked) */
  &:after {
    content: "";
    position: absolute;
    display: none;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: rgb(39, 37, 37);
  }
}
.expiry-cvc-row {
  display: flex;
  margin-bottom: 25px;
}
.row-item {
  flex: 1 0 0;
  margin-right: 15px;
  &:last-child {
    margin-right: 0;
  }
}
.redirect-message {
  margin-bottom: 30px;
  font-weight: 400;
}
.button-processing {
  pointer-events: none;
}
.loading span {
  animation-name: blink;
  animation-duration: 1.4s;
  animation-iteration-count: infinite;
  animation-fill-mode: both;
}
.loading span:nth-child(2) {
  animation-delay: 0.2s;
}
.loading span:nth-child(3) {
  animation-delay: 0.4s;
}

@media only screen and (min-width: 700px) {
  .ns-stripe-donations-container {
    padding: 50px 15px 60px;
    max-width: 782px;
  }
  .stripe-form {
    flex-direction: row;
  }
  .separator {
    display: block;
  }
}
@keyframes blink {
  0% {
    opacity: 0.2;
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0.2;
  }
}
</style>
